// `apps/web` provider tree.
//
// Order matters:
//  1. `<I18nProvider>`  — every other provider can render localised strings.
//  2. `<ThemeProvider>` — exposes resolved theme to deeply nested children
//     (DS components also work standalone via `useTheme`).
//  3. `<ApiClientProvider>` — singleton `ApiClient` shared with the router
//     context so `beforeLoad` can probe `/auth/me` without React hooks.
//  4. `<QueryClientProvider>` — react-query cache for hooks.
//  5. `<RouterProvider>` — TanStack Router; rendered last so it sees every
//     provider above.
//  6. Wave 5a (5a.2) global primitives: `<GlobalToaster>` (US-050),
//     `<GlobalConfirmModal>` (US-051), `<GlobalDrawer>` (US-052) — wrap the
//     DS Toast / Dialog / Drawer primitives behind imperative bus APIs so
//     feature pages don't have to thread state for shell-level concerns.

import { useMemo, type ReactNode } from 'react';

import { ApiClientProvider, UserHubProvider } from '@barel/api-client';
import type { ApiClient, UserHubClientOptions } from '@barel/api-client';
import { useMe } from '@barel/api-client/react';
import { I18nProvider } from '@barel/i18n';
import type { Locale } from '@barel/i18n';
import { ThemeProvider } from '@barel/ui';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';

import { GlobalConfirmModal } from '@/features/shell/GlobalConfirmModal';
import { GlobalDrawer } from '@/features/shell/GlobalDrawer';
import { GlobalToaster } from '@/features/shell/GlobalToaster';
import { createAppRouter } from '@/router';
import { createQueryClient } from '@/lib/queryClient';
import { SentryErrorBoundary } from '@/lib/sentry';

export interface AppProps {
  apiClient: ApiClient;
  apiBaseUrl: string;
  initialLocale?: Locale;
  /** Override hooks for tests — pass a pre-built query client. */
  queryClient?: QueryClient;
  /**
   * Override realtime config — tests pass `null` to skip the realtime
   * client entirely (the inbox listener becomes a no-op). Production
   * spreads the cookie-based options the WSS endpoint expects.
   */
  userHubOptions?: UserHubClientOptions | null;
}

function ErrorFallback({ resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div role="alert" className="m-4 rounded-md border border-default p-4 text-secondary">
      <p>Something went wrong loading the app.</p>
      <button
        type="button"
        onClick={resetError}
        className="mt-2 rounded-md border border-default px-3 py-1 text-sm"
      >
        Retry
      </button>
    </div>
  );
}

function FallbackBoundary({ children }: { children: ReactNode }) {
  return <SentryErrorBoundary fallback={ErrorFallback}>{children}</SentryErrorBoundary>;
}

/**
 * Auth-gated realtime — the WS handshake rides on the session cookie, so an
 * unauthenticated visitor (e.g. on `/auth/*`) would get a 401 reject + a
 * console-spamming reconnect loop. Gate `enabled` on the `me` query: connect
 * only once a user is confirmed, tear down when the session goes away
 * (sign-out clears the cache → `me` refetches → 401 → flag flips false).
 * Must live below `<QueryClientProvider>` so `useMe` can run.
 */
function AuthGatedUserHubProvider({
  options,
  children,
}: {
  options?: UserHubClientOptions;
  children: ReactNode;
}) {
  // Skip the probe entirely when there's no realtime config to gate
  // (tests pass `userHubOptions={null}` → no client, no query).
  const me = useMe({ enabled: options !== undefined });
  const authed = options !== undefined && me.isSuccess && Boolean(me.data.user);
  return (
    <UserHubProvider options={options} enabled={authed}>
      {children}
    </UserHubProvider>
  );
}

export function App({
  apiClient,
  apiBaseUrl,
  initialLocale,
  queryClient,
  userHubOptions,
}: AppProps) {
  const qc = useMemo<QueryClient>(() => queryClient ?? createQueryClient(), [queryClient]);

  // The router context exposes the singleton api client to `beforeLoad`. The
  // QueryClient is *not* in the context — `useQueryClient()` is the canonical
  // accessor inside React.
  const router = useMemo(() => createAppRouter(apiClient), [apiClient]);

  // Realtime: when the caller didn't pass an explicit option, default to a
  // cookie-authed WS against the API base URL. Tests pass `null` to skip.
  const hubOptions = useMemo<UserHubClientOptions | undefined>(() => {
    if (userHubOptions === null) return undefined;
    if (userHubOptions) return userHubOptions;
    if (typeof globalThis.WebSocket === 'undefined') return undefined;
    return {
      baseUrl: apiBaseUrl,
      onMessage: () => {
        /* per-event listeners drive the cache; nothing to do at the global level. */
      },
    };
  }, [apiBaseUrl, userHubOptions]);

  return (
    <FallbackBoundary>
      <I18nProvider locale={initialLocale}>
        <ThemeProvider>
          <ApiClientProvider options={{ baseUrl: apiBaseUrl }} client={apiClient}>
            <QueryClientProvider client={qc}>
              <AuthGatedUserHubProvider options={hubOptions}>
                <RouterProvider router={router} />
                <GlobalToaster />
                <GlobalConfirmModal />
                <GlobalDrawer />
              </AuthGatedUserHubProvider>
            </QueryClientProvider>
          </ApiClientProvider>
        </ThemeProvider>
      </I18nProvider>
    </FallbackBoundary>
  );
}
